import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import type { ProjectListItem } from '../../types';
import { useCalculatorStore } from '../../store/calculatorStore';
import { useProjects } from '../../hooks/useSchema';
import { projectLogo } from '../../utils/projectLogos';

export function ProjectSelector() {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectProject = useCalculatorStore((s) => s.selectProject);
  const { listProjects } = useProjects();

  useEffect(() => {
    let cancelled = false;
    listProjects()
      .then((list) => {
        if (!cancelled) setProjects(list);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load projects');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listProjects]);

  const handleSelectProject = (projectId: string) => {
    selectProject(projectId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 4 }}>
      <Typography variant="h4" gutterBottom align="center" fontWeight={600}>
        Select a Project
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        align="center"
        sx={{ mb: 4 }}
      >
        Choose a project to estimate AWS costs for your deployment.
      </Typography>

      <Grid container spacing={3}>
        {projects.map((project) => (
          <Grid item xs={12} sm={6} key={project.id}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSelectProject(project.id)}
                sx={{ height: '100%', p: 1 }}
              >
                <CardContent>
                  {projectLogo(project.id) ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 48,
                        mb: 2,
                      }}
                    >
                      <Box
                        component="img"
                        src={projectLogo(project.id)}
                        alt={project.name}
                        sx={{ height: 34, maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, height: 48 }}
                    >
                      <Box
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          p: 1.5,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FolderIcon fontSize="large" />
                      </Box>
                      <Typography variant="h6" fontWeight={600}>
                        {project.name}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {project.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {projects.length === 0 && (
        <Alert severity="info" sx={{ mt: 4 }}>
          No projects available. Please contact your administrator.
        </Alert>
      )}
    </Box>
  );
}
